import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { ArrowDownUp, ArrowLeft, Bot, CheckCheck, Paperclip, Search, Send, StickyNote, X } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import { inboxDialogs, inboxQuickReplies } from "@/mocks/inbox";
import { locale, t } from "@/lib/i18n";
import { useAuth } from "./auth";
import { MAX_UPLOAD_MB, conversationsApi, dayLabel, hm, inboxArchive, listTime, type Conversation, type Message } from "./inbox-api";
import { snippetsApi } from "./snippets-api";
import { systemText } from "./inboxapp-pane";
import { onServerEvent } from "@/lib/events";
import { apiErrorText } from "@/lib/api";

/* Окно «Диалоги»: слева список диалогов с фильтрами и поиском, справа переписка.
   Для вошедшего пользователя — живые данные API «Входящих» (список, переписка, отправка, заметки, шаблоны);
   для гостя — демо-переписка из src/mocks/inbox (об этом честно написано под полем ввода). */

type MsgKind = "in" | "out" | "note" | "system";
type Msg = { kind: MsgKind; text: string; time: string; at?: string; via?: string; author?: string };
type Dialog = {
  id: string;
  name: string;
  company: string;
  channel: string;
  phone: string;
  nick: string;
  owner: string;
  bot: boolean;
  unread: number;
  time: string;
  at: string;
  tags: string[];
  messages: Msg[];
};

type Filter = "all" | "waiting" | "bot";

const SOURCE = inboxDialogs as unknown as Dialog[];

const now = () =>
  new Date().toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });

const channelName = (c: string) => (c === "telegram" ? "Telegram" : "WhatsApp");
const channelIcon = (c: string) => (c === "telegram" ? "ri-telegram-line" : "ri-whatsapp-line");

/** каналы, в которые можно ответить из общего чата */
const CHANNELS = [
  { id: "whatsapp", label: "WhatsApp" },
  { id: "telegram", label: "Telegram" },
];

/** метка времени диалога в мс: по ней и сортируем список */
const ts = (d: Dialog) => {
  const t = Date.parse(d.at);
  return Number.isFinite(t) ? t : 0;
};

/** контакт клиента: в Telegram показываем ник, в WhatsApp — телефон */
const contactOf = (d: Dialog) => (d.channel === "telegram" && d.nick ? `@${d.nick}` : d.phone);
const isBotOwner = (owner: string) => /^(бот|bot)/i.test(owner);

/** диалог API → строка окна; переписка подгружается при открытии, до этого — последнее сообщение из сводки */
const fromApi = (c: Conversation): Dialog => ({
  id: c.id,
  name: c.name,
  company: c.company || c.channelName,
  channel: c.channel,
  phone: c.phone ?? "",
  nick: c.phone ? "" : c.externalId.replace(/^[a-z]+:/, ""),
  owner: c.assignee ?? (c.bot ? t("Бот {name}", { name: c.bot }) : t("Не назначен")),
  bot: !c.assigneeId && Boolean(c.bot),
  unread: c.unread,
  time: listTime(c.lastMessageAt),
  at: c.lastMessageAt ?? c.createdAt,
  tags: [c.category, c.status === "closed" ? t("Закрыт") : ""].filter(Boolean),
  messages: c.lastMessageText ? [{ kind: c.lastMessageKind || "in", text: c.lastMessageText, time: hm(c.lastMessageAt), at: c.lastMessageAt ?? undefined }] : [],
});

const msgFromApi = (m: Message): Msg => ({
  kind: m.kind,
  text: m.kind === "system" ? systemText(m) : m.text || m.media?.name || t("Вложение"),
  time: hm(m.sentAt),
  at: m.sentAt,
  author: m.author ?? undefined,
});

const initials = (n: string) =>
  n
    .split(/\s+/)
    .filter((x) => /^\p{L}/u.test(x))
    .map((x) => x[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "•";

/** последнее «живое» сообщение: по нему понимаем, ждёт ли клиент ответа */
const lastReal = (d: Dialog) => [...d.messages].reverse().find((m) => m.kind === "in" || m.kind === "out");
const isWaiting = (d: Dialog) => lastReal(d)?.kind === "in";

function ChannelBadge({ channel, className }: { channel: string; className?: string }) {
  return (
    <span className={cn("grid place-items-center", className)} aria-hidden>
      <i className={cn("text-[11px]", channel === "telegram" ? "ri-telegram-line" : "ri-whatsapp-line")} />
    </span>
  );
}

function Avatar({ dialog, size = "md" }: { dialog: Dialog; size?: "md" | "sm" }) {
  return (
    <span className="relative flex-none">
      <span className={cn("grid place-items-center rounded-full bg-primary/15 font-bold text-primary", size === "md" ? "size-10 text-sm" : "size-9 text-xs")}>
        {initials(dialog.name)}
      </span>
      <ChannelBadge
        channel={dialog.channel}
        className={cn(
          "absolute -bottom-1 -end-1 rounded-full bg-card ring-1 ring-border",
          size === "md" ? "size-5" : "size-4",
          dialog.channel === "telegram" ? "text-foreground/60" : "text-emerald-400",
        )}
      />
    </span>
  );
}

/* Каждое сообщение подписано: клиент — слева, с его именем; ответ — справа, с именем сотрудника (или бота), который ответил. */
function Bubble({ msg, author, peer }: { msg: Msg; author?: string; peer: string }) {
  if (msg.kind === "system") {
    return (
      <div className="my-1 flex justify-center">
        <span className="flash-mono max-w-[85%] rounded-full bg-muted px-3 py-1 text-center text-[11px] text-muted-foreground">{msg.text}</span>
      </div>
    );
  }
  if (msg.kind === "note") {
    return (
      <div className="max-w-[85%] self-start rounded-card rounded-bl-sm border border-amber-400/40 bg-amber-400/10 px-3.5 py-2.5">
        <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-amber-300">
          <StickyNote className="size-3.5" aria-hidden />
          {t("Заметка для команды")}
          {msg.author ? ` · ${msg.author}` : ""}
        </span>
        <p className="mt-1 text-sm leading-relaxed text-foreground/90">{msg.text}</p>
        <span className="mt-1 block text-[10px] text-muted-foreground">{msg.time}</span>
      </div>
    );
  }
  const out = msg.kind === "out";
  return (
    <div
      className={cn(
        "max-w-[85%] rounded-card px-3.5 py-2.5 shadow-card md:max-w-[75%]",
        out ? "self-end rounded-br-sm bg-primary text-primary-foreground" : "self-start rounded-bl-sm bg-muted text-foreground",
      )}
    >
      <span className={cn("mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide", out ? "justify-end text-primary-foreground/70" : "text-muted-foreground")}>
        <i className={cn("text-[11px]", out ? "ri-customer-service-2-line" : "ri-user-3-line")} aria-hidden />
        {out ? msg.author || author || t("Сотрудник") : peer}
      </span>
      <p className="text-sm leading-relaxed">{msg.text}</p>
      <span className={cn("mt-1 flex flex-wrap items-center justify-end gap-1.5 text-[10px]", out ? "text-primary-foreground/70" : "text-muted-foreground")}>
        {out && msg.via ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-primary-foreground/20 px-1.5 py-0.5 font-semibold text-primary-foreground">
            <i className={cn("text-[10px]", channelIcon(msg.via))} aria-hidden />
            {channelName(msg.via)}
          </span>
        ) : null}
        {msg.time}
        {out ? <CheckCheck className="size-3.5" aria-hidden /> : null}
      </span>
    </div>
  );
}

function InboxDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const { user } = useAuth();
  const live = Boolean(user); // вошедший видит настоящие диалоги, гость — демо
  const [threads, setThreads] = useState<Dialog[]>(() => (user ? [] : SOURCE.map((d) => ({ ...d, tags: [...d.tags], messages: d.messages.map((m) => ({ ...m })) }))));
  const [activeId, setActiveId] = useState(user ? "" : SOURCE[0]?.id || "");
  const [quick, setQuick] = useState<string[]>(user ? [] : inboxQuickReplies);
  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState<Filter>("all");
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState("");
  const [noteMode, setNoteMode] = useState(false);
  const [file, setFile] = useState<File | null>(null); // вложение любого формата: фото, видео, аудио, документ (до MAX_UPLOAD_MB)
  const fileInput = useRef<HTMLInputElement>(null);
  const [sort, setSort] = useState<"new" | "old">("new");
  const [sendVia, setSendVia] = useState<string>(SOURCE[0]?.channel || "whatsapp");
  const [mobileOpen, setMobileOpen] = useState(false);
  const feedRef = useRef<HTMLDivElement>(null);

  const active = threads.find((d) => d.id === activeId) || threads[0];

  // живые данные: список и переписка открытого диалога; обновление по событиям сервера
  const loadLive = useMemo(
    () => () =>
      conversationsApi
        .list({ limit: 30, sort: "new", archive: inboxArchive(user?.role) })
        .then((r) => {
          setTotal(r.total);
          setThreads((prev) => r.items.map((c) => {
            const was = prev.find((d) => d.id === c.id);
            const next = fromApi(c);
            return was && was.messages.length > 1 ? { ...next, messages: was.messages } : next;
          }));
        })
        .catch((e: unknown) => setError(apiErrorText(e))),
    [user?.role],
  );
  const loadThread = (id: string) =>
    conversationsApi
      .get(id)
      .then((full) => setThreads((prev) => prev.map((d) => (d.id === id ? { ...d, unread: 0, messages: full.messages.map(msgFromApi) } : d))))
      .catch((e: unknown) => setError(apiErrorText(e)));
  useEffect(() => {
    if (!live) return;
    void loadLive();
    snippetsApi.list().then((r) => setQuick(r.items.map((x) => x.message).filter(Boolean).slice(0, 6)), () => undefined);
    const offMsg = onServerEvent("message.new", (data) => {
      const d = data as { conversationId?: string };
      void loadLive();
      if (d.conversationId && d.conversationId === activeId) void loadThread(d.conversationId);
    });
    const offConv = onServerEvent("conversation.updated", () => void loadLive());
    return () => {
      offMsg();
      offConv();
    };
  }, [live, loadLive, activeId]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = threads.filter((d) => {
      if (filter === "waiting" && !isWaiting(d)) return false;
      if (filter === "bot" && !d.bot) return false;
      if (!q) return true;
      return `${d.name} ${d.company} ${d.phone} ${d.nick} ${d.owner}`.toLowerCase().includes(q);
    });
    // новые сверху (по умолчанию) или старые сверху — по реальной метке времени
    return list.sort((a, b) => (sort === "new" ? ts(b) - ts(a) : ts(a) - ts(b)));
  }, [threads, filter, query, sort]);

  const counts = useMemo(
    () => ({ all: threads.length, waiting: threads.filter(isWaiting).length, bot: threads.filter((d) => d.bot).length }),
    [threads],
  );

  // 每次切换/发送都把 лента прокручиваем вниз, как в настоящем мессенджере
  useEffect(() => {
    const el = feedRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [activeId, threads, mobileOpen]);

  const open = (id: string) => {
    setActiveId(id);
    setMobileOpen(true);
    setNoteMode(false);
    const target = threads.find((d) => d.id === id);
    if (target) setSendVia(target.channel);
    setThreads((prev) => prev.map((d) => (d.id === id ? { ...d, unread: 0 } : d)));
    if (live) {
      void loadThread(id);
      conversationsApi.read(id).catch(() => undefined);
    }
  };

  const send = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const text = draft.trim();
    if (!active) return;
    if (live && file && !noteMode) {
      setError("");
      const f = file;
      setFile(null);
      setDraft("");
      conversationsApi
        .upload(active.id, f, text)
        .then(() => loadThread(active.id))
        .catch((e: unknown) => setError(apiErrorText(e)));
      return;
    }
    if (!text) return;
    if (live) {
      setError("");
      conversationsApi
        .send(active.id, text, noteMode ? "note" : "out")
        .then(() => loadThread(active.id))
        .catch((e: unknown) => setError(apiErrorText(e)));
      setDraft("");
      setNoteMode(false);
      return;
    }
    setThreads((prev) =>
      prev.map((d) =>
        d.id === active.id
          ? {
              ...d,
              time: now(),
              at: new Date().toISOString(),
              messages: [...d.messages, { kind: noteMode ? "note" : "out", text, time: now(), at: new Date().toISOString(), author: user?.name ?? undefined, ...(noteMode ? {} : { via: sendVia }) } as Msg],
            }
          : d,
      ),
    );
    setDraft("");
    setNoteMode(false);
  };

  const toggleBot = () => {
    if (!active) return;
    const toBot = !active.bot;
    setThreads((prev) =>
      prev.map((d) =>
        d.id === active.id
          ? {
              ...d,
              bot: toBot,
              owner: toBot ? t("Бот «Входящие»") : t("Вы"),
              time: now(),
              at: new Date().toISOString(),
              messages: [...d.messages, { kind: "system", text: toBot ? t("Диалог передан боту") : t("Диалог возвращён продавцу"), time: now(), at: new Date().toISOString() } as Msg],
            }
          : d,
      ),
    );
  };

  const tabs: { id: Filter; label: string; count: number }[] = [
    { id: "all", label: t("Все"), count: counts.all },
    { id: "waiting", label: t("Без ответа"), count: counts.waiting },
    { id: "bot", label: t("Бот"), count: counts.bot },
  ];

  return (
    <Band id="inbox" tone="tint">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* верхняя полоска «окна приложения» */}
        <div className="flex items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">{str(brand) || "Hub"} / {t("входящие")}</span>
          <span className="ms-auto inline-flex items-center gap-2 whitespace-nowrap text-[11px] text-muted-foreground">
            <span className="size-1.5 rounded-full bg-emerald-400" aria-hidden />
            {live ? t("{n} диалогов · живые данные", { n: total }) : t("2 канала · онлайн")}
          </span>
        </div>

        <div className="grid lg:grid-cols-[19rem_minmax(0,1fr)]">
          {/* ── список диалогов ── */}
          <div className={cn("flex-col border-border lg:flex lg:border-e", mobileOpen && "max-lg:hidden")}>
            <div className="flex flex-col gap-3 border-b border-border p-3">
              <label className="relative block">
                <span className="sr-only">{t("Поиск по диалогам")}</span>
                <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("Имя, телефон или @ник")}
                  className="h-10 w-full box-border rounded-btn border border-border bg-background ps-9 pe-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                />
              </label>
              <div className="flex gap-1 rounded-btn bg-muted p-1">
                {tabs.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setFilter(t.id)}
                    aria-pressed={filter === t.id}
                    className={cn(
                      "flex flex-1 items-center justify-center gap-1 whitespace-nowrap rounded-[6px] px-1.5 py-1.5 text-[11px] font-semibold transition",
                      filter === t.id ? "bg-primary text-primary-foreground shadow-card" : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {t.label}
                    <span className={cn("tabular-nums", filter === t.id ? "text-primary-foreground/70" : "opacity-60")}>{t.count}</span>
                  </button>
                ))}
              </div>
            </div>

            <ul className="flex max-h-[360px] flex-1 flex-col overflow-y-auto lg:max-h-none">
              {visible.map((d) => {
                const last = lastReal(d);
                const on = d.id === active?.id;
                return (
                  <li key={d.id}>
                    <button
                      type="button"
                      onClick={() => open(d.id)}
                      className={cn(
                        "flex w-full items-start gap-3 border-b border-border/50 px-3.5 py-3 text-start transition hover:bg-muted/60",
                        on && "bg-muted",
                      )}
                    >
                      <Avatar dialog={d} />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="truncate text-sm font-semibold">{d.name}</span>
                          <span className="ms-auto whitespace-nowrap text-[10px] text-muted-foreground">{d.time}</span>
                        </span>
                        <span className="mt-0.5 flex items-center gap-1.5">
                          <span className="truncate text-xs text-muted-foreground">{last ? last.text : t("Нет сообщений")}</span>
                          {d.unread > 0 ? (
                            <span className="grid size-4 flex-none place-items-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground tabular-nums">{d.unread}</span>
                          ) : null}
                        </span>
                        <span className="mt-1.5 flex flex-wrap items-center gap-1">
                          <span className="rounded-full bg-background/70 px-2 py-0.5 text-[10px] font-medium text-muted-foreground ring-1 ring-border">{d.company}</span>
                          <span className="inline-flex items-center gap-1 rounded-full bg-background/70 px-2 py-0.5 text-[10px] font-medium text-foreground/70 ring-1 ring-border">
                            <i className={cn("text-[10px]", channelIcon(d.channel))} aria-hidden />
                            {channelName(d.channel)} · {contactOf(d)}
                          </span>
                          {d.bot ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">
                              <Bot className="size-2.5" aria-hidden />
                              {t("Бот")}
                            </span>
                          ) : null}
                          {isWaiting(d) ? <span className="rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-semibold text-amber-300">{t("Ждёт ответа")}</span> : null}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
              {visible.length === 0 ? (
                <li className="px-4 py-10 text-center text-sm text-muted-foreground">
                  {live && threads.length === 0 ? t("Диалогов пока нет — подключите WhatsApp в «Каналах».") : t("Ничего не нашлось. Измените фильтр или запрос.")}
                </li>
              ) : null}
            </ul>

            <div className="mt-auto flex items-center justify-between gap-2 border-t border-border px-3.5 py-2.5 text-[10px] text-muted-foreground">
              <span className="tabular-nums">
                {t("{shown} из {total} диалогов", { shown: visible.length, total: threads.length })}
              </span>
              <button
                type="button"
                onClick={() => setSort((v) => (v === "new" ? "old" : "new"))}
                aria-pressed={sort === "old"}
                title={sort === "new" ? t("Сейчас новые сверху — переключить на старые") : t("Сейчас старые сверху — переключить на новые")}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-border px-2.5 py-1 font-semibold text-foreground/80 transition hover:border-primary/50 hover:text-foreground"
              >
                <ArrowDownUp className={cn("size-3 transition-transform", sort === "old" && "rotate-180")} aria-hidden />
                {sort === "new" ? t("Новые сверху") : t("Старые сверху")}
              </button>
            </div>
          </div>

          {/* ── переписка ── */}
          <div className={cn("flex min-h-0 flex-col", !mobileOpen && "max-lg:hidden")}>
            {active ? (
              <>
                <div className="border-b border-border">
                  <div className="flex flex-wrap items-center gap-2.5 px-3.5 py-3">
                    <button
                      type="button"
                      onClick={() => setMobileOpen(false)}
                      aria-label={t("К списку диалогов")}
                      className="grid size-9 flex-none place-items-center rounded-btn border border-border text-muted-foreground hover:bg-muted lg:hidden"
                    >
                      <ArrowLeft className="size-4" aria-hidden />
                    </button>
                    <Avatar dialog={active} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="flex flex-wrap items-center gap-2 truncate text-sm font-semibold">
                        {active.name}
                        {active.bot ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">
                            <Bot className="size-2.5" aria-hidden />
                            {t("Бот")}
                          </span>
                        ) : null}
                      </p>
                      <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                        <span className="inline-flex items-center gap-1 whitespace-nowrap font-semibold text-foreground">
                          <i
                            className={cn("text-[12px]", active.channel === "telegram" ? "ri-telegram-line text-sky-500" : "ri-whatsapp-line text-emerald-500")}
                            aria-hidden
                          />
                          {contactOf(active)}
                        </span>
                        <span className="inline-flex items-center gap-1 whitespace-nowrap">
                          <i className="ri-building-line text-[11px]" aria-hidden />
                          {active.company}
                        </span>
                        <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-muted px-2 py-0.5 font-semibold text-foreground">
                          <i className={cn("text-[11px]", channelIcon(active.channel))} aria-hidden />
                          {t("Канал: {channel}", { channel: channelName(active.channel) })}
                        </span>
                      </p>
                    </div>
                    {/* кто ведёт диалог с нашей стороны */}
                    <div className="flex flex-none items-center gap-2 rounded-btn border border-border bg-background/60 px-2.5 py-1.5">
                      <span
                        className={cn(
                          "grid size-7 flex-none place-items-center rounded-full text-[10px] font-bold",
                          isBotOwner(active.owner) ? "bg-primary/15 text-primary" : "bg-primary/10 text-primary ring-1 ring-primary/25",
                        )}
                        aria-hidden
                      >
                        {isBotOwner(active.owner) ? <Bot className="size-3.5" /> : initials(active.owner)}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[9px] uppercase tracking-wider text-muted-foreground">{t("Отвечает")}</span>
                        <span className="block max-w-[9rem] truncate text-[11px] font-semibold text-foreground">{active.owner}</span>
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 border-t border-border/60 px-3.5 py-2">
                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
                      {active.tags.map((t) => (
                        <span key={t} className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium whitespace-nowrap text-muted-foreground">
                          {t}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setNoteMode((v) => !v)}
                        aria-pressed={noteMode}
                        title={t("Внутренняя заметка: клиент её не увидит")}
                        className={cn(
                          "inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-btn border px-2.5 text-[11px] font-semibold transition",
                          noteMode ? "border-amber-400/60 bg-amber-400/15 text-amber-300" : "border-border text-muted-foreground hover:bg-muted",
                        )}
                      >
                        <StickyNote className="size-3.5" aria-hidden />
                        <span className="max-sm:hidden">{t("Заметка")}</span>
                      </button>
                      {live ? null : (
                      <button
                        type="button"
                        onClick={toggleBot}
                        className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-2.5 text-[11px] font-semibold text-muted-foreground transition hover:bg-muted hover:text-foreground"
                      >
                        <Bot className="size-3.5" aria-hidden />
                        <span className="max-sm:hidden">{active.bot ? t("Забрать у бота") : t("Передать боту")}</span>
                      </button>
                      )}
                    </div>
                  </div>
                </div>

                <div ref={feedRef} className="flex h-[340px] flex-col gap-2.5 overflow-y-auto bg-background/40 p-3.5 md:h-[400px] md:p-5">
                  {active.messages.map((m, i) => {
                    const prev = active.messages[i - 1];
                    const newDay = Boolean(m.at) && (!prev?.at || new Date(prev.at).toDateString() !== new Date(m.at as string).toDateString());
                    return (
                      <div key={`${active.id}-${i}-${m.time}`} className="contents">
                        {newDay ? (
                          <div className="my-1 flex justify-center">
                            <span className="rounded-full bg-background px-2.5 py-0.5 text-[10px] font-medium text-muted-foreground ring-1 ring-border" data-testid="message-day">
                              {dayLabel(m.at as string)}
                            </span>
                          </div>
                        ) : null}
                        <Bubble msg={m} author={isBotOwner(active.owner) ? active.owner : undefined} peer={active.name} />
                      </div>
                    );
                  })}
                </div>

                <div className="border-t border-border p-3 md:p-4">
                  {noteMode || live ? null : (
                    <div className="mb-2 flex flex-wrap items-center gap-1.5">
                      <span className="inline-flex items-center gap-1 me-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        <Send className="size-3" aria-hidden />
                        {t("Отправить в")}
                      </span>
                      {CHANNELS.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setSendVia(c.id)}
                          aria-pressed={sendVia === c.id}
                          className={cn(
                            "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold transition",
                            sendVia === c.id ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted hover:text-foreground",
                          )}
                        >
                          <i className={cn("text-[11px]", channelIcon(c.id))} aria-hidden />
                          {c.label}
                        </button>
                      ))}
                      <span className="ms-auto hidden text-[10px] text-muted-foreground sm:block">
                        {sendVia === active.channel ? t("канал клиента") : t("сообщение уйдёт в {channel}", { channel: channelName(sendVia) })}
                      </span>
                    </div>
                  )}
                  <div className="mb-2 flex gap-1.5 overflow-x-auto pb-1">
                    {quick.map((q) => (
                      <button
                        key={q}
                        type="button"
                        onClick={() => setDraft(q)}
                        className="whitespace-nowrap rounded-full border border-border bg-background px-3 py-1.5 text-[11px] text-muted-foreground transition hover:border-primary/50 hover:text-foreground"
                      >
                        {q.length > 34 ? `${q.slice(0, 34)}…` : q}
                      </button>
                    ))}
                  </div>
                  {file ? (
                    <div className="mb-2 flex items-center gap-2 rounded-btn border border-border bg-background px-3 py-1.5 text-[12px]">
                      <Paperclip className="size-3.5 flex-none text-muted-foreground" aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{file.name}</span>
                      <span className="text-muted-foreground">{file.size > 1024 * 1024 ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(file.size / 1024))} KB`}</span>
                      <button type="button" onClick={() => setFile(null)} aria-label={t("Убрать файл")} className="grid size-6 place-items-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground">
                        <X className="size-3.5" aria-hidden />
                      </button>
                    </div>
                  ) : null}
                  <form onSubmit={send} className="flex items-end gap-2">
                    {live && !noteMode ? (
                      <>
                        <input
                          ref={fileInput}
                          type="file"
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) {
                              if (f.size > MAX_UPLOAD_MB * 1024 * 1024) setError(t("Файл больше {n} МБ — отправьте ссылкой или сожмите.", { n: MAX_UPLOAD_MB }));
                              else {
                                setError("");
                                setFile(f);
                              }
                            }
                            e.target.value = "";
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => fileInput.current?.click()}
                          aria-label={t("Прикрепить файл")}
                          title={t("Фото, видео, аудио или документ любого формата — до {n} МБ", { n: MAX_UPLOAD_MB })}
                          className="grid size-11 flex-none place-items-center rounded-btn border border-border bg-background text-muted-foreground transition hover:border-primary/50 hover:text-foreground"
                        >
                          <Paperclip className="size-4" aria-hidden />
                        </button>
                      </>
                    ) : null}
                    <input
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      maxLength={500}
                      placeholder={noteMode ? t("Внутренняя заметка — клиент её не увидит") : file ? t("Подпись к файлу (необязательно)") : t("Написать в {channel}", { channel: channelName(sendVia) })}
                      className={cn(
                        "h-11 min-w-0 flex-1 box-border rounded-btn border bg-background px-3.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2",
                        noteMode ? "border-amber-400/60 focus:border-amber-400 focus:ring-amber-400/25" : "border-border focus:border-primary focus:ring-primary/25",
                      )}
                    />
                    <button
                      type="submit"
                      disabled={!draft.trim() && !(file && live && !noteMode)}
                      aria-label={noteMode ? t("Сохранить заметку") : t("Отправить сообщение")}
                      className="grid size-11 flex-none place-items-center rounded-btn bg-primary text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-45"
                    >
                      <Send className="size-4" aria-hidden />
                    </button>
                  </form>
                  <p className={cn("mt-2 text-[11px]", error ? "text-red-500" : "text-muted-foreground")}>
                    {error || (live ? t("Живые данные: сообщения уходят клиенту. Полный интерфейс — во «Входящих».") : t("Демо-режим: сообщения остаются в этом макете и не уходят клиенту."))}
                  </p>
                </div>
              </>
            ) : (
              <div className="grid h-full min-h-[320px] place-items-center p-6 text-center text-sm text-muted-foreground">{t("Выберите диалог слева")}</div>
            )}
          </div>
        </div>
      </div>
    </Band>
  );
}

export default InboxDemo;